// API Service for centralized API communication with full backend integration
import { API_CONFIG } from '../../config/api';
import { STORAGE_KEYS } from '../../config/storageKeys';
import { stripAppBasename, withAppBasename } from '../../config/appBase';

// API Error Class
export class APIError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

// Helper function for delays
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Base API class with full backend integration
export class APIService {
  /** One in-flight refresh for all APIService instances (avoids refresh-token rotation races). */
  static _refreshInFlight = null;

  constructor(config = {}) {
    this.baseURL = config.baseURL || API_CONFIG.baseURL;
    this.timeout = config.timeout || API_CONFIG.timeout;
    this.retryAttempts = config.retryAttempts || API_CONFIG.retryAttempts;
    this.retryDelay = config.retryDelay || API_CONFIG.retryDelay;
  }

  /** Decode JWT exp (ms) without verifying signature — used only for proactive refresh timing. */
  static getAccessTokenExpiryMs(token) {
    if (!token || typeof token !== 'string') return 0;
    try {
      const part = token.split('.')[1];
      if (!part) return 0;
      const padded = part.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(padded));
      return payload.exp ? payload.exp * 1000 : 0;
    } catch {
      return 0;
    }
  }

  // Get auth tokens from storage
  getAccessToken() {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || 
           sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  getRefreshToken() {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || 
           sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /** Prefer the storage that already holds tokens so refresh does not orphan/rotate into the other. */
  _resolveRememberMe() {
    if (localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true') return true;
    if (localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) {
      return true;
    }
    // Migrate sessionStorage-only sessions onto localStorage on refresh so
    // mobile tab discard does not silently drop the refresh token.
    if (sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) {
      return true;
    }
    return false;
  }

  // Store tokens (always clear the other storage so stale JWTs cannot shadow the active pair)
  storeTokens(accessToken, refreshToken, rememberMe = false) {
    const storage = rememberMe ? localStorage : sessionStorage;
    const other = rememberMe ? sessionStorage : localStorage;
    storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    other.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    other.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (rememberMe) {
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    }
  }

  // Clear tokens
  clearTokens() {
    [localStorage, sessionStorage].forEach(storage => {
      Object.values(STORAGE_KEYS).forEach(key => {
        storage.removeItem(key);
      });
    });
  }

  /**
   * Terminal auth failure for real API calls only: wipe JWTs and show the
   * session-expired overlay. Must NOT be used from proactive/background renew —
   * that was ending sessions early while the refresh token was still valid.
   */
  notifySessionExpired() {
    this.clearTokens();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('sessionExpired'));
    }
  }

  /**
   * Refresh before access token expires (important for multi-hour LNAT full-exam runs).
   * @param {number} bufferMs — refresh when expiry is within this window (default 5 min)
   */
  async ensureAuthFresh(bufferMs = 5 * 60 * 1000) {
    const token = this.getAccessToken();
    if (!token) {
      // Soft path: try refresh if we still have one, but never end the session here.
      if (this.getRefreshToken()) {
        return this.refreshAccessToken({ terminal: false });
      }
      return null;
    }
    const expMs = APIService.getAccessTokenExpiryMs(token);
    if (!expMs) return token;
    // Renew whenever we are inside the buffer — keeps idle tabs (phone locked,
    // laptop asleep, tablet backgrounded) from hitting a hard 401 later.
    if (Date.now() >= expMs - bufferMs) {
      return this.refreshAccessToken({ terminal: false });
    }
    return token;
  }

  // Refresh access token using refresh token (serialized across parallel 401s / polls)
  // terminal:true  → clear tokens on 401/403 (used by makeRequest / user actions)
  // terminal:false → leave tokens alone on failure (proactive background renew)
  async refreshAccessToken({ terminal = true } = {}) {
    if (APIService._refreshInFlight) {
      return APIService._refreshInFlight;
    }
    APIService._refreshInFlight = this._performRefreshAccessToken({ terminal });
    try {
      return await APIService._refreshInFlight;
    } finally {
      APIService._refreshInFlight = null;
    }
  }

  async _performRefreshAccessToken({ terminal = true } = {}) {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      if (terminal) {
        this.clearTokens();
      }
      throw new APIError('No refresh token available', 401);
    }

    try {
      const response = await fetch(`${this.baseURL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (terminal && (response.status === 401 || response.status === 403)) {
          this.clearTokens();
        }
        throw new APIError(
          errorData.error || 'Token refresh failed',
          response.status,
          errorData
        );
      }

      const data = await response.json();
      const { access_token, refresh_token } = data;
      if (!access_token) {
        throw new APIError('Token refresh failed', 500, data);
      }

      const rememberMe = this._resolveRememberMe();
      this.storeTokens(
        access_token,
        refresh_token || refreshToken,
        rememberMe
      );

      if (data.must_change_password === true || data.must_change_password === false) {
        const storage = rememberMe ? localStorage : sessionStorage;
        let stored = {};
        try {
          const raw = storage.getItem(STORAGE_KEYS.USER_DATA);
          const parsed = raw ? JSON.parse(raw) : {};
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            stored = parsed;
          }
        } catch (e) {
          stored = {};
        }
        stored.must_change_password = data.must_change_password === true;
        storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(stored));
        if (
          data.must_change_password === true
          && typeof window !== 'undefined'
          && stripAppBasename(window.location.pathname) !== '/set-password'
        ) {
          window.location.assign(withAppBasename('/set-password'));
        }
      }

      return access_token;
    } catch (error) {
      if (
        terminal
        && error instanceof APIError
        && (error.status === 401 || error.status === 403)
      ) {
        this.clearTokens();
      }
      throw error;
    }
  }

  // Create request config
  createRequestConfig(options = {}) {
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      ...options,
    };

    // FormData must use multipart boundaries set by the browser. Forcing
    // Content-Type: application/json breaks parsing; Flask then sees empty form fields
    // (e.g. studio-cases domain_notes never reaches the server).
    if (config.body instanceof FormData) {
      if (!config.headers || typeof config.headers !== 'object') {
        config.headers = {};
      }
      delete config.headers['Content-Type'];
    }

    // Add auth token if available and not skipped
    const token = this.getAccessToken();
    if (token && !config.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add timeout using AbortController
    const controller = new AbortController();
    const timeoutMs = (config.timeout !== undefined ? config.timeout : this.timeout);
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    config.signal = controller.signal;
    config._timeoutId = timeoutId;

    return config;
  }

  // Make HTTP request with retry logic and auto token refresh
  async makeRequest(endpoint, options = {}, attempt = 1) {
    const url = `${this.baseURL}${endpoint}`;
    const config = this.createRequestConfig(options);

    try {
      const response = await fetch(url, config);
      clearTimeout(config._timeoutId);

      // Parse response
      let data = {};
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const textData = await response.text();
        if (textData) {
          data = { message: textData };
        }
      }

      // Handle token expiration (401) with auto-refresh (single serialized refresh)
      if (
        response.status === 401
        && !options.skipAuth
        && !options.isRefreshRequest
        && !options.isAuthRetry
      ) {
        const hasRefreshToken = Boolean(this.getRefreshToken());
        if (hasRefreshToken) {
          try {
            await this.refreshAccessToken({ terminal: true });
            return this.makeRequest(endpoint, { ...options, isAuthRetry: true }, attempt);
          } catch (refreshError) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('Token refresh failed:', refreshError?.message || refreshError);
              }

              // Only show the expiry overlay when the refresh token itself is dead.
              // Transient network/5xx failures must not wipe an otherwise valid session.
              const refreshStatus = refreshError?.status;
              if (refreshStatus === 401 || refreshStatus === 403) {
                this.notifySessionExpired();
              }

              throw new APIError('Authentication failed', 401, { requiresLogin: true });
            }
        }
        // Authenticated request got 401 with no refresh token — show overlay.
        this.notifySessionExpired();
        throw new APIError('Authentication required', 401, { requiresLogin: true });
      }

      if (response.status === 403 && data.error === 'password_change_required') {
        if (typeof window !== 'undefined' && stripAppBasename(window.location.pathname) !== '/set-password') {
          window.location.assign(withAppBasename('/set-password'));
        }
        throw new APIError(
          data.error || data.message || `HTTP error! status: ${response.status}`,
          response.status,
          data
        );
      }

      // Check if response is ok
      if (!response.ok) {
        throw new APIError(
          data.error || data.message || `HTTP error! status: ${response.status}`,
          response.status,
          data
        );
      }

      return data;
    } catch (error) {
      clearTimeout(config._timeoutId);

      // Handle network errors and retries
      if (attempt < this.retryAttempts && this.shouldRetry(error, options) && !options.isRetry) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Request failed, retrying... (${attempt}/${this.retryAttempts})`, error.message);
        }
        await delay(this.retryDelay * attempt);
        return this.makeRequest(endpoint, options, attempt + 1);
      }

      throw error;
    }
  }

  // Determine if request should be retried
  shouldRetry(error, options = {}) {
    // Never retry auth requests (login, register, magic link) or requests explicitly marked skipRetry
    if (options.skipAuth || options.skipRetry) {
      return false;
    }

    // Don't retry client request or authentication errors (400, 401, 403, 404, 422)
    if (error.status >= 400 && error.status < 500) {
      return false;
    }
    
    // Retry on network errors or 5xx server errors
    if (error.name === 'TypeError' || error.name === 'AbortError') {
      return true;
    }
    if (error.status >= 500 && error.status <= 599) {
      return true;
    }
    
    return false;
  }

  // HTTP Methods
  async get(endpoint, options = {}) {
    return this.makeRequest(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data = null, options = {}) {
    const config = { ...options, method: 'POST' };
    if (data) {
      if (options.isFormData || data instanceof FormData) {
        config.body = data;
        // createRequestConfig strips Content-Type when body is FormData
      } else {
        config.body = JSON.stringify(data);
      }
    }
    return this.makeRequest(endpoint, config);
  }

  async put(endpoint, data, options = {}) {
    return this.makeRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint, data, options = {}) {
    return this.makeRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint, options = {}) {
    return this.makeRequest(endpoint, { ...options, method: 'DELETE' });
  }

  // Upload file
  async upload(endpoint, file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);

    const config = {
      ...options,
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData
        ...options.headers,
      },
    };

    // Remove Content-Type to let browser set it with boundary
    delete config.headers['Content-Type'];

    return this.makeRequest(endpoint, config);
  }
}

export default APIService;